import type {BusinessAllowedFeature} from '@/types/AfriexBusiness';

export type Action =
  | {type: 'SELECT_FEATURE'; payload: {key: string}}
  | {type: 'UNSELECT_FEATURE'; payload: {key: string}}
  | {type: 'SET_TREE'; payload: BusinessAllowedFeature[]};

type State = {
  tree: BusinessAllowedFeature[];
};

const updateFeatureRecursive = (
  features: BusinessAllowedFeature[],
  targetKey: string,
  targetState: boolean, // true for select, false for unselect
): BusinessAllowedFeature[] => {
  return features.map((feature) => {
    // 1. Found the target node
    if (feature.key === targetKey) {
      // If we unselect a parent, we usually unselect all children too
      const updatedChildren =
        targetState === false && feature.children
          ? feature.children.map((c) => ({...c, isSelected: false}))
          : feature.children;

      return {...feature, isSelected: targetState, children: updatedChildren};
    }

    // 2. Not the target, check children
    if (feature.children && feature.children.length > 0) {
      const updatedChildren = updateFeatureRecursive(
        feature.children,
        targetKey,
        targetState,
      );

      if (updatedChildren !== feature.children) {
        // Determine if parent should be selected based on children
        // Selection: If ANY child is true, parent is true.
        // Unselection: If NO children are true, parent becomes false.
        const isAnyChildSelected = updatedChildren.some(
          (child) => child.isSelected,
        );

        return {
          ...feature,
          children: updatedChildren,
          isSelected: targetState
            ? isAnyChildSelected || feature.isSelected
            : isAnyChildSelected,
        };
      }
    }

    return feature;
  });
};

export const allowedFeatureReducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'SELECT_FEATURE':
      return {
        ...state,
        tree: updateFeatureRecursive(state.tree, action.payload.key, true),
      };

    case 'UNSELECT_FEATURE':
      return {
        ...state,
        tree: updateFeatureRecursive(state.tree, action.payload.key, false),
      };

    default:
      return state;
  }
};
